import React, { FC } from 'react';
import { DetailsSection } from '@app/screens/nft-marketplace/MakeOfferDialog/components/DetailsSection';
import { formatUnits } from 'ethers/lib/utils';
import { Box, HStack, Text, VStack } from '@chakra-ui/react';
import { DropdownMenu } from '@app/components/DropdownMenu';
import { offerDurationItems } from '@app/screens/nft-marketplace/MakeOfferDialog/helpers';
import { add, format } from 'date-fns';
import { AmountInput } from '@app/screens/nft-marketplace/MakeOfferDialog/components/AmountInput';
import { SubmitSection } from '@app/screens/nft-marketplace/MakeOfferDialog/components/SubmitSection';
import { NFTMarketplaceListing } from '@app/types/nftMarketplace';
import { useGetBestBidForListing } from '@app/hooks/nft/useGetBestBidForListing';
import { useMakeAnOffer } from '@app/hooks/nft/useMakeAnOffer';
import { FormProvider, useForm } from 'react-hook-form';
import { showSuccessToast } from '@app/components/Toast';
import { ExternalLink } from '@app/components/ExternalLink';
import { Token } from '@app/types/token';
import { getAuctionDescription } from '@app/helpers/nft/getAuctionDescription';
import { yupResolver } from '@hookform/resolvers/yup';
import { buyNowWithBidsSchema } from '@app/screens/nft-marketplace/MakeOfferDialog/components/BuyItNowWithBidsAuction/helpers';
import { useAddress } from '@thirdweb-dev/react';
import { useTokenBalance } from '@app/hooks/token/useTokenBalance';
import { formatNumber, parseUnits } from '@app/helpers/format';
import { ethers } from 'ethers';
import { FormControl } from '@app/components/FormControl';
import { useKpNftPositionDetails } from '@app/screens/positions/components/SpNftPositionListRowItem/hooks';
import { useDiscountValue } from '@app/hooks/nft/useDiscountValue';
import { useNftMarketplaceFiltersState } from '@app/state/nftMarketplaceFiltersStore';

interface Props {
  listing: NFTMarketplaceListing;
  sellCurrency: Token;
  onClose: () => void;

  tokenId: string;
  lpTokenId: string;
  poolAddress: string;
}

type Form = {
  duration: string;
  amount: string;
  token: Token;
};

export const BuyItNowWithBidsAuction: FC<Props> = ({
  listing,
  sellCurrency,
  onClose,
  tokenId,
  lpTokenId,
  poolAddress
}) => {
  const address = useAddress();
  const { data: bestBidForListing } = useGetBestBidForListing(listing.id);
  const { balance } = useTokenBalance(
    sellCurrency.contractAddress,
    address ?? ''
  );
  const { ownedPremiumNFTTokenId } = useNftMarketplaceFiltersState();

  const { usdAmount, data } = useKpNftPositionDetails(
    tokenId,
    poolAddress,
    lpTokenId
  );

  const discount = useDiscountValue(listing, sellCurrency, usdAmount);

  const { makeAnOffer, isLoading } = useMakeAnOffer();
  const methods = useForm<Form>({
    defaultValues: {
      duration: '3',
      amount: '',
      token: sellCurrency
    },
    resolver: yupResolver(buyNowWithBidsSchema),
    context: {
      reservePrice: formatUnits(listing.reserve, sellCurrency.decimals),
      bestBidForListing: bestBidForListing
        ? formatUnits(bestBidForListing.amount, sellCurrency.decimals)
        : 0,
      balance
    }
  });
  const {
    watch,
    setValue,
    handleSubmit,
    formState: { errors }
  } = methods;

  const duration = watch('duration');
  const amount = watch('amount');
  const token = watch('token');

  const onSubmit = async (values: Form) => {
    const expiryTimestamp = add(new Date(), {
      days: +values.duration
    }).getTime();
    const extraData = ethers.utils.defaultAbiCoder.encode(
      ['uint'],
      [expiryTimestamp]
    );

    const txHash = await makeAnOffer({
      listingId: listing.id,
      amount: parseUnits(values.amount, sellCurrency.decimals),
      optionalPremiumTokenId: listing.nftMetadata.isPremium
        ? ownedPremiumNFTTokenId
        : '0',
      extraData
    });

    if (txHash) {
      showSuccessToast(
        `Transaction successful`,
        <ExternalLink txHash={txHash} />
      );

      onClose();
    }
  };

  return (
    <FormProvider {...methods}>
      <VStack
        as="form"
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        alignItems="flex-start"
        gap={5}
        mt={6}
      >
        <DetailsSection
          auctionType={listing.type}
          auctionDescription={getAuctionDescription(listing.type)}
          bestOffer={
            bestBidForListing?.amount && sellCurrency
              ? `${formatUnits(
                  bestBidForListing?.amount,
                  sellCurrency?.decimals
                )} ${sellCurrency.symbol}`
              : '-'
          }
          balance={
            balance
              ? `${formatNumber(+balance, 3)} ${sellCurrency.symbol}`
              : '-'
          }
          reservePrice={
            listing.reserve && sellCurrency
              ? `${formatUnits(listing.reserve, sellCurrency?.decimals)} ${
                  sellCurrency.symbol
                }`
              : '-'
          }
          discount={discount}
          deposit={data?.amount}
          depositUsd={usdAmount}
        />

        <HStack gap={4} mt={6} width="100%">
          <DropdownMenu
            items={offerDurationItems}
            title="Duration"
            onSelect={val => {
              setValue('duration', val, { shouldValidate: true });
            }}
            selected={duration}
          />
          <Text
            fontSize={14}
            sx={{
              height: '44px',
              background: 'black',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              padding: '4px 12px',
              color: 'gray.15'
            }}
          >
            {duration
              ? format(
                  add(new Date(), { days: +duration }),
                  'dd MMM yyyy HH:mm'
                )
              : '-'}
          </Text>
        </HStack>

        <FormControl name="amount">
          <AmountInput
            label="Price"
            handleTokenSelection={val => {
              setValue('token', val, { shouldValidate: true });
            }}
            handleValueChange={val => {
              setValue('amount', val, { shouldValidate: true });
            }}
            value={amount}
            token={token}
          />
        </FormControl>

        <SubmitSection />
      </VStack>
    </FormProvider>
  );
};
